import { type PasswordSubject, passwordRuleStates, passwordRuleText } from '@acasa/shared'

/**
 * The password requirements, ticking off as they are met.
 *
 * Evaluated by `passwordRuleStates` from `@acasa/shared` — the same function the
 * API calls before it stores anything. A second copy of the rules in the browser
 * is how you end up with a form showing all green and a server that refuses.
 *
 * Nothing turns red. An unmet rule while somebody is still typing is not an
 * error, it is a requirement they have not reached yet, and colouring it as a
 * failure makes filling in a form feel like getting things wrong.
 */
export function PasswordRules({
  password,
  subject,
}: {
  password: string
  subject: PasswordSubject
}) {
  const rules = passwordRuleStates(password, subject)
  const untouched = password.length === 0

  return (
    <div className="text-xs">
      <ul className="flex flex-col gap-1">
        {rules.map((rule) => (
          <li key={rule.id} className="flex items-start gap-2">
            <Mark ok={rule.ok} untouched={untouched} />
            <span className={rule.ok && !untouched ? 'text-positive' : 'text-muted'}>
              {rule.label}
            </span>
          </li>
        ))}
      </ul>

      <p className="text-faint mt-2 leading-relaxed">{passwordRuleText}</p>
    </div>
  )
}

/**
 * A tick once met, a hollow dot until then. Drawn rather than typed: the app is
 * set in Wix Madefor, and ✓/○ from a fallback font next to it reads as a
 * different typeface every time.
 */
function Mark({ ok, untouched }: { ok: boolean; untouched: boolean }) {
  const met = ok && !untouched

  return (
    <svg
      viewBox="0 0 12 12"
      aria-hidden
      className={`mt-[3px] size-3 shrink-0 ${met ? 'text-positive' : 'text-faint'}`}
    >
      {met ? (
        <path
          d="M2.5 6.4l2.4 2.4 4.6-5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ) : (
        <circle cx="6" cy="6" r="2.4" fill="none" stroke="currentColor" strokeWidth="1.2" />
      )}
    </svg>
  )
}
